import React, { useState, useEffect, useRef } from "react";
import Header from "../components/Header.js";
import Footer from "../components/Footer.js";
import { Link } from "react-router-dom";
// Importing Lucide icons for a cleaner look
import { CheckCircle, Eye, Target, Calendar, Users, Map, Star, Plus } from 'lucide-react';

// Custom Hook for the count-up animation
const useCountUp = (end, duration = 2000) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const isVisible = useOnScreen(ref);

    useEffect(() => {
        if (isVisible) {
            let start = 0;
            // Calculate a step time to ensure the animation takes the defined duration
            const stepTime = Math.max(1, Math.abs(Math.floor(duration / end)));
            const timer = setInterval(() => {
                start += 1;
                setCount(start);
                if (start >= end) {
                    clearInterval(timer);
                    setCount(end); // Ensure it stops exactly at 'end'
                }
            }, stepTime);
            return () => clearInterval(timer);
        }
    }, [isVisible, end, duration]);

    return [count, ref];
};

// Custom hook to check if an element is on screen
const useOnScreen = (ref) => {
    const [isIntersecting, setIntersecting] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => setIntersecting(entry.isIntersecting),
            { threshold: 0.1 } // Trigger when 10% of element is visible
        );
        if (ref.current) {
            observer.observe(ref.current);
        }
        return () => {
            if (ref.current) {
                observer.unobserve(ref.current);
            }
        };
    }, [ref]);

    return isIntersecting;
};

// --- Reusable Sub-Components ---

const StatItem = ({ value, label, description, index }) => {
    const [count, ref] = useCountUp(value, 3000); // Increased duration for dramatic effect
    const isVisible = useOnScreen(ref);

    return (
        <div 
            ref={ref} 
            className={`transition-all duration-700 ease-out transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            style={{ transitionDelay: `${index * 150}ms` }}
        >
            <div className="text-4xl lg:text-6xl font-extrabold mb-2">{count}{label.includes('Properties') || label.includes('Spaces') ? '' : '+'}</div>
            <div className="text-lg font-semibold opacity-95">{label}</div>
            <div className="text-sm opacity-80 mt-1 max-w-[200px] mx-auto">{description}</div>
        </div>
    );
};

const InfoCard = ({ icon, title, children }) => (
    <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg border border-gray-100 flex flex-col items-center text-center transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
        <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mb-6">
            {React.cloneElement(icon, { className: 'w-8 h-8 text-white' })}
        </div>
        <h3 className="text-xl font-extrabold mb-4 uppercase text-gray-900">{title}</h3>
        {children}
    </div>
);

const TimelineEvent = ({ year, title, children }) => {
    const ref = useRef(null);
    const isVisible = useOnScreen(ref);
    
    return (
        <div 
            ref={ref} 
            className={`text-left transition-all duration-700 ease-out transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
            <div className="text-orange-600 font-extrabold text-4xl mb-2">{year}</div>
            <div className="font-semibold text-base uppercase tracking-wider text-gray-800 mb-2">
                {title}
            </div>
            <div className="w-16 h-1 bg-orange-500 mb-3"></div>
            <p className="text-sm text-gray-600 leading-relaxed">{children}</p>
        </div>
    );
};

const ProcessStep = ({ number, title, description }) => (
    <div className="flex items-start mb-8 border-l-2 border-gray-200 pl-6 relative last:mb-0">
        <div className="absolute left-[-11px] top-0 w-5 h-5 bg-orange-500 rounded-full border-4 border-white"></div>
        <div className="flex-shrink-0 mr-6">
            <h4 className="font-bold text-2xl text-orange-500 mb-2">{number}</h4>
        </div>
        <div>
            <h4 className="font-bold text-xl text-gray-900 mb-1">{title}</h4>
            <p className="text-gray-600">{description}</p>
        </div>
    </div>
);

const TeamMemberCard = ({ name, role, image, index }) => {
    const ref = useRef(null);
    const isVisible = useOnScreen(ref);

    return (
        <div 
            ref={ref} 
            className={`text-center bg-white p-4 rounded-xl shadow-sm transition-all duration-700 ease-out transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            style={{ transitionDelay: `${index * 150}ms` }}
        >
            <div className="bg-gray-200 h-48 w-full rounded-lg mb-4 overflow-hidden">
                {/* Use the provided image prop or placeholder */}
                {image ? (
                    <img src={image} alt={name} className="w-full h-full object-cover"/>
                ) : (
                    <Users className="w-12 h-12 text-gray-400 mx-auto mt-16" />
                )}
            </div>
            <h5 className="font-bold text-lg text-gray-900">{name}</h5>
            <p className="text-orange-600 text-sm font-medium">{role}</p>
        </div>
    );
};

const TestimonialCard = ({ title, text, name, index }) => {
    const ref = useRef(null);
    const isVisible = useOnScreen(ref);

    return (
        <div
            ref={ref}
            className={`bg-white p-6 rounded-xl shadow-lg border border-gray-100 h-full flex flex-col transition-all duration-700 ease-in-out ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
            style={{ transitionDelay: `${index * 100}ms` }}
        >
            <div className="flex text-yellow-500 mb-4">
                {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-current" />
                ))}
            </div>
            <h4 className="font-bold text-lg mb-2 text-gray-900">{title}</h4>
            <p className="text-gray-600 text-sm mb-6 flex-grow italic">"{text}"</p>
            <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center mr-3 flex-shrink-0">
                    <Users className="w-5 h-5 text-orange-500" />
                </div>
                <span className="font-semibold text-gray-800">{name}</span>
            </div>
        </div>
    );
};

const FaqItem = ({ question, answer, index }) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef(null);
    const isVisible = useOnScreen(ref);

    return (
        <div
            ref={ref}
            className={`border-b border-gray-200 transition-all duration-700 ease-in-out ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            } ${isOpen ? 'bg-orange-50' : 'bg-white'}`}
            style={{ transitionDelay: `${index * 100}ms` }}
        >
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex justify-between items-center text-left py-4 sm:py-6"
            >
                <h4 className={`font-semibold text-lg ${isOpen ? 'text-orange-600' : 'text-gray-900'}`}>{question}</h4>
                <Plus
                    className={`text-orange-500 w-6 h-6 transform transition-transform duration-300 flex-shrink-0 ml-4 ${
                        isOpen ? "rotate-45" : "rotate-0"
                    }`}
                />
            </button>
            <div
                className={`overflow-hidden transition-all duration-500 ease-in-out ${
                    isOpen ? "max-h-96 pb-4 sm:pb-6" : "max-h-0"
                }`}
            >
                <p className="text-gray-600 leading-relaxed pr-4">{answer}</p>
            </div>
        </div>
    );
};

// --- Main AboutUs Component ---

const AboutUs = () => {
    // 1. Add state to control the initial animation
    const [isLoaded, setIsLoaded] = useState(false);

    // 2. Use useEffect to set the state after the component mounts
    useEffect(() => {
        // Use a small timeout to ensure the transition can occur after the initial render
        const timer = setTimeout(() => {
            setIsLoaded(true);
        }, 50);

        return () => clearTimeout(timer);
    }, []);

    const faqData = [
        {
            question: "How do I apply for a rental property?",
            answer:
                "You can apply directly through our website on the property's listing page. Simply click the 'Apply Now' button and fill out the online application form. Our team will guide you through any additional steps.",
        },
        {
            question: "What documents do I need to rent a property?",
            answer:
                "Typically, you will need a valid ID (passport or driver's license), proof of income (payslips or bank statements), and references from previous landlords. We will provide a specific checklist when you apply.",
        },
        {
            question: "How long does the approval process take?",
            answer:
                "The approval process usually takes between 24 to 72 hours, provided all your documentation is complete and your references are reachable. We strive to process applications as quickly as possible.",
        },
        {
            question: "Do you require a deposit?",
            answer:
                "Yes, a security deposit, typically equivalent to one month's rent, is required to secure the property. This deposit is refundable at the end of your lease, pending a property inspection.",
        },
        {
            question: "What should I do if I need maintenance or repairs?",
            answer:
                "All maintenance requests can be submitted through our online tenant portal. For emergencies, we provide a 24/7 contact number. We have a dedicated team to address repairs promptly.",
        },
    ];

    return (
        <div className="min-h-screen bg-white font-sans">
            <Header />

            {/* 3. Wrap all main content sections in a single container and apply the transition classes */}
            <div 
                className={`
                    transition-all duration-1000 ease-out transform 
                    ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}
                `}
            >
                {/* Hero Section - ENHANCED */}
                <section className="bg-gray-100 pt-24 sm:pt-32 pb-16 relative overflow-hidden">
                    {/* ABOUT US text moved above the image section */}
                    <div className="container max-w-7xl mx-auto px-6 mb-8 lg:mb-12">
                        <div className="flex items-center">
                            <div className="w-4 h-4 bg-orange-500 rounded-full mr-3 animate-pulse"></div>
                            <span className="text-sm text-orange-600 font-semibold uppercase tracking-widest">
                                ABOUT US
                            </span>
                        </div>
                    </div>

                    {/* Image and "Who We Are" text section */}
                    <div className="container max-w-7xl mx-auto px-6 relative z-10">
                        <div className="lg:w-full relative rounded-3xl overflow-hidden h-80 md:h-[400px] ">
                            <img
                                src="https://images.unsplash.com/photo-1484154218962-a197022b5858?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80"
                                alt="Bright and clean kitchen in a rental home"
                                className="w-full h-full object-cover"
                            />
                            {/* Overlay for the title with a subtle gradient for readability */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/100 to-transparent flex items-end p-8 sm:p-10">
                                <h1 className="text-4xl md:text-5xl font-extrabold text-white leading-tight">
                                    Who We Are: <span className="text-orange-500">Your Trusted Rental Partner.</span>
                                </h1>
                            </div>
                        </div>
                    </div>

                    {/* Description text and button below the image */}
                    <div className="container max-w-7xl mx-auto px-6 relative z-10 mt-12 lg:mt-16">
                        <div className="flex flex-col lg:flex-row items-start gap-12">
                            <div className="lg:w-1/2">
                                <p className="text-gray-700 leading-relaxed text-lg mb-8">
                                    At <strong className="text-black-600">Raulfex Pty Ltd</strong>, we make finding the right rental property simple, affordable, and stress-free. We provide well-managed rental solutions that fit your lifestyle and budget, prioritizing transparency and ease every step of the way.
                                </p>
                                <Link to="/properties" className="inline-block bg-orange-600 text-white font-bold py-3 px-8 rounded-full shadow-lg hover:bg-orange-700 transition-colors duration-300 transform hover:scale-[1.02]">
                                    Find Your Next Home &rarr;
                                </Link>
                            </div>
                             {/* Empty div for spacing on large screens, or for a second image if desired */}
                            <div className="lg:w-1/2 hidden lg:block"></div> 
                        </div>
                    </div>
                </section>

                {/* Stats Section - Increased CountUp values for a more impact */}
                <section className="bg-orange-500 py-16">
                    <div className="container max-w-7xl mx-auto px-6">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-white text-center">
                            <StatItem
                                index={0}
                                value={250} // Increased value
                                label="Rental Properties"
                                description="Apartments, townhouses & family homes"
                            />
                            <StatItem
                                index={1}
                                value={15} // Increased value
                                label="Commercial Spaces"
                                description="Offices, shops & warehouse units"
                            />
                            <StatItem
                                index={2}
                                value={98}
                                label="Client Satisfaction"
                                description="Percentage of happy tenants and owners"
                            />
                            <StatItem
                                index={3}
                                value={7}
                                label="Major Locations"
                                description="Johannesburg, Pretoria, Benoni & more"
                            />
                        </div>
                    </div>
                </section>

                {/* Mission, Vision, Values Section */}
                <section className="bg-gray-50 py-20">
                    <div className="container max-w-7xl mx-auto px-6">
                        <h2 className="text-4xl font-extrabold text-center text-gray-900 mb-4">Our Core Principles</h2>
                        <p className="text-gray-600 text-center max-w-2xl mx-auto mb-12">
                            The foundation of Raulfex is built on clear objectives and unwavering values.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <InfoCard
                                title="Mission"
                                icon={<CheckCircle />}
                            >
                                <p className="text-gray-600 text-center leading-relaxed">
                                    To provide tenants with secure, affordable, and well-managed rental properties while making the process as smooth and transparent as possible.
                                </p>
                            </InfoCard>

                            <InfoCard
                                title="Vision"
                                icon={<Eye />}
                            >
                                <p className="text-gray-600 text-center leading-relaxed">
                                    To become the most trusted property rental partner in Gauteng, known for reliability, digital efficiency, and customer-first service excellence.
                                </p>
                            </InfoCard>

                            <InfoCard
                                title="Values"
                                icon={<Target />}
                            >
                                <ul className="text-gray-600 space-y-2 text-left w-full">
                                    {["Trust & Transparency", "Simplicity & Ease", "Tenant Care", "Professional Excellence"].map((value) => (
                                        <li key={value} className="flex items-center justify-center sm:justify-start">
                                            <Star className="w-4 h-4 text-orange-500 mr-2 flex-shrink-0" />
                                            {value}
                                        </li>
                                    ))}
                                </ul>
                            </InfoCard>
                        </div>
                    </div>
                </section>

                {/* Our Story Section - This section has been re-purposed as the main narrative after the hero */}
                <section className="bg-gray-50 py-20">
                    <div className="container max-w-7xl mx-auto px-6">
                        <div className="flex flex-col lg:flex-row items-center gap-16">
                            <div className="lg:w-1/2 order-2 lg:order-1">
                                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                                    The Journey Behind Raulfex
                                </h2>
                                <p className="text-gray-700 leading-relaxed max-w-2xl">
                                    Raulfex was founded with one goal in mind: to make property rentals more transparent and accessible. We noticed how overwhelming the process could be, from endless searching to complicated paperwork and hidden fees. We prioritize people before paperwork. Today, we connect tenants to trusted rental properties with efficiency and care.
                                </p>
                                <Link 
                                    to="/about-us#timeline" 
                                    className="inline-block text-orange-600 font-bold hover:underline transition-colors duration-300 mt-6"
                                >
                                    See our full history &rarr;
                                </Link>
                            </div>
                            <div className="lg:w-1/2 order-1 lg:order-2">
                                <img
                                    src="https://images.unsplash.com/photo-1582407947304-fd86f028f716?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80"
                                    alt="Beautiful suburban house with a green lawn"
                                    className="w-full h-80 object-cover rounded-3xl shadow-xl"
                                />
                            </div>
                        </div>
                    </div>
                </section>

                {/* History / Timeline Section */}
                <section id="timeline" className="bg-white py-20"> {/* Added id for linking */}
                    <div className="container max-w-7xl mx-auto px-6 text-center">
                        <h2 className="text-4xl font-extrabold text-black mb-4">
                            A Legacy of Trust and Growth
                        </h2>
                        <p className="text-gray-700 max-w-3xl mx-auto mb-16">
                            From a small vision to a trusted real estate partner, our journey has been defined by dedication, growth, and client success.
                        </p>

                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 max-w-6xl mx-auto">
                            <TimelineEvent year="2017" title="Founded">
                                Raulfex Pty Ltd was officially registered with the vision of simplifying property rentals and making the rental process fair for tenants.
                            </TimelineEvent>
                            <TimelineEvent year="2019" title="First Properties Managed">
                                We began managing our first portfolio of rental properties, focusing on quality homes and reliable tenant support.
                            </TimelineEvent>
                            <TimelineEvent year="2021" title="Expanding Services">
                                Our offerings grew to include a wide variety of rental options, serving students, families, and professionals across diverse communities.
                            </TimelineEvent>
                            <TimelineEvent year="2025" title="Leading Today">
                                Raulfex continues to expand while staying committed to secure, affordable, and well-located rentals, making the process stress-free for every tenant.
                            </TimelineEvent>
                        </div>
                    </div>
                </section>

                {/* Process Section - Enhanced layout */}
                <section className="bg-gray-50 py-20">
                    <div className="container max-w-7xl mx-auto px-6">
                        <div className="flex flex-col lg:flex-row items-center gap-16">
                            {/* Image / Visual */}
                            <div className="lg:w-1/2 order-2 lg:order-1 mt-10 lg:mt-0">
                                <img
                                    src="https://images.unsplash.com/photo-1576941089067-2de3c901e126?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1578&q=80"
                                    alt="Happy family in their new home"
                                    className="rounded-2xl shadow-2xl object-cover w-full h-[450px]"
                                />
                            </div>

                            {/* Process Steps */}
                            <div className="lg:w-1/2 order-1 lg:order-2">
                                <div className="flex items-center mb-4">
                                    <div className="w-4 h-4 bg-orange-500 rounded-full mr-3"></div>
                                    <span className="text-sm text-gray-600 uppercase tracking-widest font-semibold">
                                        OUR PROCESS
                                    </span>
                                </div>
                                <h2 className="text-4xl font-extrabold text-gray-900 mb-10">
                                    Simple Steps to Your Dream Home
                                </h2>

                                <ProcessStep
                                    number="01"
                                    title="Discover & Search"
                                    description="Start by browsing our curated listings and use our advanced filters to find properties that match your exact needs."
                                />
                                <ProcessStep
                                    number="02"
                                    title="Viewing & Application"
                                    description="Schedule a viewing at your convenience and submit your application with necessary documents through our secure portal."
                                />
                                <ProcessStep
                                    number="03"
                                    title="Lease & Onboarding"
                                    description="Once approved, we guide you through the lease signing and ensure a smooth, worry-free handover of the keys."
                                />
                                <ProcessStep
                                    number="04"
                                    title="Enjoy & Support"
                                    description="Move in and enjoy your new home! Our property management team is always on hand for support and maintenance."
                                />

                                <Link 
                                    to="/properties" 
                                    className="inline-block bg-gray-900 text-white font-bold py-3 px-8 rounded-full hover:bg-gray-800 transition-colors duration-300 mt-6"
                                >
                                    View Properties
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Team Section */}
                <section className="bg-white py-20">
                    <div className="container max-w-7xl mx-auto px-6">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12">
                            <h2 className="text-4xl font-extrabold text-black lg:w-1/2 mb-4 md:mb-0">
                                Dedicated Experts, Working for You
                            </h2>
                            <p className="text-gray-600 lg:w-1/3">
                                Behind every successful property journey is a team of professionals committed to guiding you with trust, expertise, and care.
                            </p>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            <TeamMemberCard index={0} name="Alex Johnson" role="Lead Agent" image="https://images.unsplash.com/photo-1531427186611-ecfd6d936c79?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80" />
                            <TeamMemberCard index={1} name="Maria Garcia" role="Property Manager" image="https://images.unsplash.com/photo-1494790108755-2616b612b0ec?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80" />
                            <TeamMemberCard index={2} name="Sam Chen" role="Client Relations" image="https://images.unsplash.com/photo-1506794778202-dfa9291d3460?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80" />
                            <TeamMemberCard index={3} name="Jessica Brown" role="Leasing Specialist" image="https://images.unsplash.com/photo-1557053910-d9eadeed1c21?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80" />
                        </div>
                    </div>
                </section>

                {/* Testimonials Section */}
                <section className="bg-gray-100 py-20">
                    <div className="container max-w-7xl mx-auto px-6 text-center">
                        <h2 className="text-4xl font-extrabold text-black mb-4">
                            What Our Clients Say
                        </h2>
                        <p className="text-gray-600 max-w-2xl mx-auto mb-12">
                            Trusted by tenants and property owners who value reliability and care.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 text-left">
                            <TestimonialCard
                                index={0}
                                title="Seamless & Quick"
                                text="Truly dedicated and reliable. From the first meeting to closing, their support made the entire process stress-free."
                                name="Mia F. - Apartment Tenant"
                            />
                            <TestimonialCard
                                index={1}
                                title="Professional Service"
                                text="Every detail was handled with care. Their team made us feel confident and informed during the whole process."
                                name="David S. - Home Owner"
                            />
                            <TestimonialCard
                                index={2}
                                title="Found the Perfect Place"
                                text="Finding a home seemed overwhelming, but their guidance made it easy, clear, and enjoyable from start to finish."
                                name="Olivia C. - Student Rental"
                            />
                            <TestimonialCard
                                index={3}
                                title="Above and Beyond"
                                text="They went the extra mile to find the perfect home for our family. We're thrilled with the results and they exceeded our expectations."
                                name="Peter H. - Family Home"
                            />
                        </div>
                    </div>
                </section>

                {/* FAQ Section - Improved Mobile layout */}
                <section className="bg-white py-20">
                    <div className="container max-w-7xl mx-auto px-6">
                        <div className="flex flex-col lg:flex-row gap-16 items-start">
                            {/* FAQ Left Column */}
                            <div className="lg:w-1/3 sticky top-20"> {/* Made it sticky for large screens */}
                                <h2 className="text-4xl font-extrabold text-gray-900 mb-4">
                                    Frequently Asked Questions
                                </h2>
                                <p className="text-gray-600 mb-8">
                                    Got questions about renting, applications, or maintenance? We've answered some of the most common ones to guide you through your rental journey.
                                </p>
                                <Link to="/contact" className="inline-block text-orange-600 font-bold hover:underline transition-colors duration-300">
                                    Still have a question? Contact us &rarr;
                                </Link>
                                
                                {/* Placeholder Image/Illustrative box */}
                                <div className="bg-orange-50 h-64 w-full rounded-2xl hidden lg:flex items-center justify-center mt-8">
                                    <Map className="w-16 h-16 text-orange-200" />
                                </div>
                            </div>

                            {/* FAQ Right Column */}
                            <div className="lg:w-2/3 w-full">
                                <div className="border-t border-gray-200">
                                    {faqData.map(
                                        (faq, index) => (
                                            <FaqItem
                                                key={index}
                                                index={index}
                                                question={faq.question}
                                                answer={faq.answer}
                                            />
                                        )
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div> 
            {/* End of animated content wrapper */}

            <Footer />
        </div>
    );
};

export default AboutUs;